import type { BoardUser } from './leaderboard.dto.js';
import { LEADERBOARD_CONTENT_MODE } from './leaderboard_player_context.js';

/** The global board (all players). Scores go there through `scores/add`. */
export const GLOBAL_BOARD_ID = `global_${LEADERBOARD_CONTENT_MODE}`;

/** Two-letter code of the player's country, or null when it is unknown. */
export function normalizeCountry( raw: string | null | undefined ): string | null
{
  const cc = ( raw ?? '' ).trim().toUpperCase();
  return /^[A-Z]{2}$/.test( cc ) ? cc : null;
}

/** The player's country as the game server reported it at login (`player_sync.ts`). */
export function playerCountry(): string | null
{
  try { return normalizeCountry( localStorage.getItem( 'player_country' ) ); }
  catch { return null; }
}

/**
 * Board of one country, as in Block Royale: the server keeps a top per country, filled by
 * `country/scores/add` with that country. It holds the country's own top 1000, which the global
 * board's top 1000 may not contain at all.
 */
export function countryBoardId( country: string ): string
{
  return `country:${country}`;
}

export interface BoardIdentity
{
  userId: number | null;
  deviceId: string;
}

function findMe( users: readonly BoardUser[] | undefined, me: BoardIdentity ): BoardUser | undefined
{
  return ( users ?? [] ).find( u => ( me.userId !== null && u.userId === me.userId ) || ( !!me.deviceId && u.deviceId === me.deviceId ) );
}

export interface CountryCatchUp
{
  /** Points to add to the country board so it matches the global one. */
  delta: number;
  /** The player has no row on the country board yet (an add of 0 puts them on it). */
  missing: boolean;
}

/**
 * The country board started empty while the global one already had everyone's stars, and a
 * country add can fail on its own: the player's country score is brought up to the global one.
 * Never negative (an add cannot take points away). Null when the player is not on the global
 * board, so there is nothing to compare with.
 */
export function countryCatchUp(
  global_users: readonly BoardUser[] | undefined,
  country_users: readonly BoardUser[] | undefined,
  me: BoardIdentity,
): CountryCatchUp | null
{
  const global_row = findMe( global_users, me );
  if ( !global_row ) return null;
  const country_row = findMe( country_users, me );
  const delta = Math.max( 0, ( global_row.ratingScore ?? 0 ) - ( country_row?.ratingScore ?? 0 ) );
  return { delta, missing: !country_row };
}
